/** @mui/material imports */
/** @mui/icons-material imports */
/** React imports */
import { Context, createContext, ReactNode, useState } from 'react';
/** Goldspot imports */
import { ColumnHeader } from '../TypeScript/types';
import { StrategyOptions } from '../TypeScript/constants';

export interface TessellationFormContextProviderProps {
  children: JSX.Element;
}

export interface TessellationFormContextType {
  depthCol: ColumnHeader;
  setDepthCol: (depth_col: ColumnHeader) => void;
  depthMinCol: ColumnHeader;
  setDepthMinCol: (depth_col: ColumnHeader) => void;
  depthMaxCol: ColumnHeader;
  setDepthMaxCol: (depth_col: ColumnHeader) => void;
  maxGap: number;
  setMaxGap: (max_gap: number) => void;
  numberOfClusters: number;
  setNumberOfClusters: (number_of_clusters: number) => void;
  inputSignals: ColumnHeader[];
  setInputSignals: (inputSignals: ColumnHeader[]) => void;
  bhidCol: ColumnHeader;
  setBhidCol: (bhid_col: ColumnHeader) => void;
  clusterMethod?: string;
  setClusterMethod: (cluster_method?: string) => void;
  propagationStrategy: StrategyOptions;
  setPropagationStrategy: (option: StrategyOptions) => void;
  pcaEnabled: boolean;
  setPcaEnabled: (pca: boolean) => void;
  bestKEnabled: boolean;
  setBestKEnabled: (bestK: boolean) => void;
  resetTessellationForm: () => void;
}

export const TessellationFormContext: Context<TessellationFormContextType> =
  createContext<TessellationFormContextType>({
    inputSignals: [],
    depthCol: '',
    setDepthCol: (depth_col: ColumnHeader): void => {},
    depthMinCol: '',
    setDepthMinCol: (depth_col: ColumnHeader): void => {},
    depthMaxCol: '',
    setDepthMaxCol: (depth_col: ColumnHeader): void => {},
    maxGap: 10,
    setMaxGap: (max_gap: number): void => {},
    numberOfClusters: 3,
    setNumberOfClusters: (number_of_clusters: number): void => {},
    setInputSignals: (inputSignals?: ColumnHeader[]): void => {},
    pcaEnabled: false,
    setPcaEnabled: (pca: boolean): void => {},
    bestKEnabled: false,
    setBestKEnabled: (bestK: boolean): void => {},
    bhidCol: '',
    setBhidCol: (bhid_col?: ColumnHeader): void => {},
    setClusterMethod: (cluster_method?: string): void => {},
    propagationStrategy: StrategyOptions.default,
    setPropagationStrategy: (option: StrategyOptions): void => {},
    resetTessellationForm: (): void => {},
  });

export const TessellationFormContextProvider: React.FC<
  TessellationFormContextProviderProps
> = (props: TessellationFormContextProviderProps): JSX.Element => {
  const [depthCol, setDepthCol] = useState<ColumnHeader>('');
  const [depthMinCol, setDepthMinCol] = useState<ColumnHeader>('');
  const [depthMaxCol, setDepthMaxCol] = useState<ColumnHeader>('');
  const [maxGap, setMaxGap] = useState<number>(10);
  const [numberOfClusters, setNumberOfClusters] = useState<number>(3);
  const [inputSignals, setInputSignals] = useState<ColumnHeader[]>([]);
  const [bhidCol, setBhidCol] = useState<ColumnHeader>('');
  const [clusterMethod, setClusterMethod] = useState<string>();
  const [propagationStrategy, setPropagationStrategy] =
    useState<StrategyOptions>(StrategyOptions.default);
  const [pcaEnabled, setPcaEnabled] = useState<boolean>(false);
  const [bestKEnabled, setBestKEnabled] = useState<boolean>(false);

  const resetTessellationForm = (): void => {
    setDepthCol('');
    setDepthMinCol('');
    setDepthMaxCol('');
    setMaxGap(10);
    setNumberOfClusters(3);
    setInputSignals([]);
    setBhidCol('');
    setClusterMethod('');
    setPropagationStrategy(StrategyOptions.default);
    setPcaEnabled(false);
    setBestKEnabled(false);
  };

  return (
    <>
      <TessellationFormContext.Provider
        value={{
          depthCol,
          setDepthCol,
          depthMinCol,
          setDepthMinCol,
          depthMaxCol,
          setDepthMaxCol,
          maxGap,
          setMaxGap,
          numberOfClusters,
          setNumberOfClusters,
          inputSignals,
          setInputSignals,
          bhidCol,
          setBhidCol,
          clusterMethod,
          setClusterMethod,
          propagationStrategy,
          setPropagationStrategy,
          pcaEnabled,
          setPcaEnabled,
          bestKEnabled,
          setBestKEnabled,
          resetTessellationForm,
        }}
      >
        {props.children}
      </TessellationFormContext.Provider>
    </>
  );
};
